import {
    BarPlot,
    ChartsAxisHighlight,
    ChartsGrid,
    ChartsTooltip,
    ChartsXAxis,
    ChartsYAxis,
    LinePlot,
    ResponsiveChartContainer,
} from "@mui/x-charts";
import { ChartsLoadingOverlay } from "@mui/x-charts/ChartsOverlay";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { currencyFormatter } from "legacyNext/utils";

import { generateChartData } from "./finance-graph.utils";
import { ChartLegend } from "../../views/ReductionAnalysisView/components";
import { LoanInvestmentSettings } from "../types";

const CHART_HEIGHT = 450;
const CHART_MARGIN = { top: 40, right: 30, left: 80, bottom: 30 };

type Props = {
    loading: boolean;
    impactYear: number | null;
    loanInvestmentSettings: LoanInvestmentSettings;
};

export const FinancingGraph = (props: Props) => {
    const { loanInvestmentSettings, impactYear, loading } = props;

    const { t } = useTranslation();

    const [series, years] = useMemo(() => {
        const currentYear = new Date().getFullYear();

        const { years, propertyLoanData, improvementLoanData, equityData } =
            generateChartData(loanInvestmentSettings, currentYear);

        const impactYearIndex = impactYear
            ? years.indexOf(impactYear.toString())
            : -1;

        const propertyValueData = propertyLoanData.map((value, index) => {
            const total =
                value + improvementLoanData[index] + equityData[index];

            if (impactYearIndex === -1 || index <= impactYearIndex)
                return total;

            return total * (1 + loanInvestmentSettings.valueImpact / 100);
        });

        const series = [
            {
                data: propertyLoanData,
                label: t("legacyNext.propertyLoan"),
                color: "#807AF8",
            },
            {
                data: improvementLoanData,
                label: t("legacyNext.improvementLoan"),
                color: "#DBD9FF",
            },
            {
                data: equityData,
                label: t("legacyNext.equity"),
                color: "#F0F0F0",
            },
            {
                data: propertyValueData,
                label: t("legacyNext.propertyValue", "property value"),
                color: "#3579F6",
                type: "line",
            },
        ].map((item) => ({
            ...item,
            data: item.data.map((value) => (isNaN(value) ? 0 : value)),
            type: item.type ?? "bar",
            stack: "total",
            valueFormatter: currencyFormatter,
        }));

        return [series, years];
    }, [loanInvestmentSettings, impactYear, t]);
    return (
        <>
            <ResponsiveChartContainer
                sx={(theme) => ({
                    "& .MuiChartsAxis-root tspan": {
                        fill: theme.palette.text.secondary,
                    },
                })}
                series={series as any}
                xAxis={[{ data: years, scaleType: "band" }]}
                yAxis={[
                    {
                        label: "kr.",
                        tickNumber: 5,
                        valueFormatter: (value: number) => {
                            const label =
                                loanInvestmentSettings.propertyPrice >= 1000000
                                    ? (value / 1000000).toString() + "M"
                                    : (value / 1000).toString() + "K";

                            return label;
                        },
                        labelStyle: {
                            transform: "translate(15px, -210px)",
                        },
                    },
                ]}
                margin={CHART_MARGIN}
                height={CHART_HEIGHT}
            >
                {loading && <ChartsLoadingOverlay />}
                <ChartsGrid horizontal />
                <BarPlot />
                <LinePlot />
                <ChartsXAxis
                    slots={{ axisTick: () => null, axisLine: () => null }}
                />
                <ChartsYAxis
                    slots={{ axisTick: () => null, axisLine: () => null }}
                />
                <ChartsTooltip
                    slotProps={{
                        popper: {
                            placement: "top",
                            sx: {
                                ".MuiChartsTooltip-mark": {
                                    width: 10,
                                    height: 10,
                                },
                                ".MuiChartsTooltip-valueCell": {
                                    textAlign: "right",
                                },
                            },
                        },
                    }}
                />
                <ChartsAxisHighlight x="band" />
            </ResponsiveChartContainer>
            <ChartLegend series={series as any} />
        </>
    );
};
